import './perks-codes.component.scss';

import { h } from 'preact';
import { useCallback, useEffect, useMemo } from 'preact/hooks';

import { QRComponent } from '../../../codes/components/qr.component';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { AppleWalletBtn } from '../../../common/components/wallet/apple-wallet-btn.component';
import { GoogleWalletBtn } from '../../../common/components/wallet/google-wallet-btn.component';
import { useTranslation } from '../../../i18n';
import type { Contract } from '../../../membership/models/membership.model';
import { WalletType } from '../../../membership/models/membership.model';
import { selectMemberships } from '../../../membership/store/membership.selectors';
import { downloadWalletPass } from '../../../membership/store/membership.thunk';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { CodeState } from '../../model/code.interface';
import { ContentType } from '../../model/tab.interface';
import { selectCodes, selectPerksLoading } from '../../store/perks.selectors';
import { loadTabContent } from '../../store/perks.thunk';

export const PerksCodesComponent = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const codes = useAppSelector(selectCodes);
  const loading = useAppSelector(selectPerksLoading);
  const memberships = useAppSelector(selectMemberships);

  useEffect(() => {
    if (!codes || !codes.length) {
      return;
    }

    const hasProcessingCodes = codes.some((code) => code.state === CodeState.PROCESSING);

    if (hasProcessingCodes) {
      const timer = setTimeout(() => {
        dispatch(loadTabContent({ contentType: ContentType.PROMOTION }));
      }, 30000);

      return () => clearTimeout(timer);
    }
  }, [codes]);

  const contract = useMemo(() => {
    const contracts = memberships
      ?.filter((membership) => !!membership.contract)
      .map((membership) => membership.contract);

    if (contracts && contracts.length === 1) {
      return contracts[0];
    }
    return null;
  }, [memberships]);

  const handleDownloadAppleWalletPass = useCallback(
    (contract: Contract) => {
      dispatch(downloadWalletPass({ contract, walletType: WalletType.APPLE }));
    },
    [dispatch]
  );

  const handleDownloadGoogleWalletPass = useCallback(
    (contract: Contract) => {
      dispatch(downloadWalletPass({ contract, walletType: WalletType.GOOGLE }));
    },
    [dispatch]
  );

  const isAndroidMobile = () => {
    return /Android/.test(navigator.userAgent);
  };

  const isAppleMobile = () => {
    return /iPhone|iPad|iPod/.test(navigator.userAgent);
  };

  if (loading) {
    return (
      <section class="single-loading">
        <SpinnerComponent />
      </section>
    );
  }

  return (
    <div class="single-perks-codes-wrapper">
      {codes && codes.length > 0 ? (
        <section class="single-card-grid">
          {codes.map((code) => (
            <div key={code.id || code.code} class="single-card single-code">
              {code.state === CodeState.ACTIVE && <QRComponent text={code.code} />}

              {code.state === CodeState.PROCESSING && (
                <div class="single-code-processing">
                  <div class="single-code-processing-overlay">
                    <SpinnerComponent />
                    <span>{t('perks.generating')}</span>
                  </div>
                  <QRComponent text="Generating QR Code" />
                </div>
              )}

              <div class="single-code-info">
                {!!code.campaignName && code.campaignName !== '' && (
                  <h3 class="single-code-name h3 single-h3">{code.campaignName}</h3>
                )}

                {!!code.campaignDescription && code.campaignDescription !== '' && (
                  <p class="single-code-description p">{code.campaignDescription}</p>
                )}

                {contract?.tier?.walletTheme?.enabled && (isAppleMobile() || isAndroidMobile()) && (
                  <div class="single-wallet-links">
                    {isAppleMobile() && (
                      <AppleWalletBtn onClick={() => handleDownloadAppleWalletPass(contract)} />
                    )}
                    {isAndroidMobile() && (
                      <GoogleWalletBtn onClick={() => handleDownloadGoogleWalletPass(contract)} />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </section>
      ) : (
        <div class="no-results">{t('perks.noCodesFound')}</div>
      )}
    </div>
  );
};
